import React, { useContext, useEffect, useState } from 'react';
import { Box, Container, Grid, Alert, Stack, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { di } from '../../../state/inversify.config';
import { UserNotificationType } from '../../../components/userNotification/userNotificationType.enum';
import { UserNotificationContext } from '../../../components/userNotification/userNotificationContext';
import { TextInput } from '../../../components/textInput/textInput';
import { Button } from '../../../components/button/button';
import { Team } from '../../../state/reduxRepositories/teams/team.interfaces';
import { ManageTeamDomainService } from './manageTeamDomainService';

interface AddTeamProps {
  setShowModal?: any;
  selectedTeam?: Team;
  isAdd: boolean;
}

export function ManageTeam({
  setShowModal,
  selectedTeam,
  isAdd,
}: AddTeamProps) {
  const { t } = useTranslation();
  const { addUserNotification } = useContext(UserNotificationContext);
  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));
  const isUpSM = useMediaQuery(theme.breakpoints.up('sm'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const modalElementSize = isUpMD || isUpSM || isLarge ? '300px' : '100%';

  const manageTeamDomainService = di.get<ManageTeamDomainService>(
    ManageTeamDomainService,
  );
  const [teamName, setTeamName] = useState('');
  const [teamNameExist, setTeamNameExist] = useState(false);
  const [successBanner, setSuccessBanner] = useState(false);
  const [teamErrorMessage, setTeamErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const teams = manageTeamDomainService.useAllTeamsListSelector();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!teamName) {
      setTeamErrorMessage(true);
      setErrorMessage(t('REQUIRED_NAME').toString());
      return;
    }
    setTeamErrorMessage(false);

    if (isAdd) {
      manageTeamDomainService.dispatchAddTeam(teamName);
    }

    if (!isAdd && selectedTeam) {
      const copySelectedTeam: Team = { ...selectedTeam };
      copySelectedTeam.name = teamName;
      manageTeamDomainService.dispatchUpdateTeam(copySelectedTeam);
    }
    setShowModal(false);
    setSuccessBanner(true);
  };

  useEffect(() => {
    if (!isAdd && selectedTeam) {
      setTeamName(selectedTeam.name);
    } else {
      setTeamName('');
    }
  }, [isAdd, selectedTeam]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTeamName(event.target.value);
    const isTeamNameExist = teams.some(
      (team) => team.name.toLowerCase() === event.target.value.toLowerCase(),
    );
    setTeamNameExist(isTeamNameExist);
    if (!isTeamNameExist) setTeamErrorMessage(false);
  };

  useEffect(() => {
    if (teamNameExist && isAdd) {
      setTeamErrorMessage(true);
      setErrorMessage(t('TEAM_ERROR_MESSAGE').toString());
    }
    if (successBanner) {
      addUserNotification(
        UserNotificationType.SUCCESS,
        isAdd ? t('TEAM_CREATED') : t('TEAM_UPDATED'),
        UserNotificationType.SUCCESS,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamNameExist, successBanner]);

  return (
    <Container maxWidth="md">
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextInput
                  sx={{ width: modalElementSize }}
                  testId="teamNameInput"
                  name="teamName"
                  type="text"
                  value={teamName}
                  label={t('ADD_TEAM_NAME')}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
            {teamErrorMessage && (
              <Stack sx={{ width: modalElementSize }}>
                <Alert
                  onClose={() => setTeamErrorMessage(false)}
                  severity="error"
                >
                  {errorMessage}
                </Alert>
              </Stack>
            )}
            <Grid container>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Button
                  testId="saveTeam"
                  disabled={teamNameExist}
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={() => {}}
                  label={isAdd ? t('CREATE') : t('UPDATE')}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
